import React, { useState } from 'react';
import { Bot, Save, Plus, X, AlertCircle, CheckCircle, Zap } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { generateCommentVariations, moderateComment } from '../../../services/aiCommentGenerator';
import { generateCommentVariations as generateGeminiCommentVariations, moderateComment as moderateGeminiComment } from '../../../services/geminiCommentGenerator';
import { generateCommentVariations as generateAnthropicCommentVariations, moderateComment as moderateAnthropicComment } from '../../../services/anthropicCommentGenerator';

const AICommentConfig = ({ onConfigSave }) => {
  const [isEnabled, setIsEnabled] = useState(true);
  const [keywords, setKeywords] = useState([]);
  const [newKeyword, setNewKeyword] = useState('');
  const [matchType, setMatchType] = useState('any');
  const [commentStyle, setCommentStyle] = useState('supportive');
  const [brandVoice, setBrandVoice] = useState('professional');
  const [maxCommentsPerHour, setMaxCommentsPerHour] = useState(10);
  const [isSaved, setIsSaved] = useState(false);
  const [aiProvider, setAiProvider] = useState('openai');
  const [testPost, setTestPost] = useState('');
  const [style, setStyle] = useState('supportive');
  const [generatedComments, setGeneratedComments] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');

  const handleAddKeyword = () => {
    if (newKeyword?.trim() && !keywords?.includes(newKeyword?.trim())) {
      setKeywords([...keywords, newKeyword?.trim()]);
      setNewKeyword('');
    }
  };

  const handleRemoveKeyword = (keyword) => {
    setKeywords(keywords?.filter(k => k !== keyword));
  };

  const handleSaveConfig = () => {
    const config = {
      isEnabled,
      keywords,
      matchType,
      commentStyle,
      brandVoice,
      maxCommentsPerHour
    };

    if (onConfigSave) {
      onConfigSave(config);
    }

    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 2000);
  };

  const handleTestComment = async () => {
    if (!testPost?.trim()) {
      setError('Please enter a test post to generate comments');
      return;
    }

    if (!commentStyle) {
      setError('Please select a comment style');
      return;
    }

    try {
      setIsGenerating(true);
      setError('');
      
      const options = {
        postContent: testPost,
        commentStyle,
        brandVoice,
        keywords: keywords,
        maxLength: 200
      };

      let variations;
      // Use selected AI provider
      if (aiProvider === 'gemini') {
        variations = await generateGeminiCommentVariations(options, 3);
      } else if (aiProvider === 'anthropic') {
        variations = await generateAnthropicCommentVariations(options, 3);
      } else {
        variations = await generateCommentVariations(options, 3);
      }
      
      // Moderate comments
      const moderatedVariations = await Promise.all(
        variations?.map(async (comment) => {
          let moderation;
          if (aiProvider === 'gemini') {
            moderation = await moderateGeminiComment(comment);
          } else if (aiProvider === 'anthropic') {
            moderation = await moderateAnthropicComment(comment);
          } else {
            moderation = await moderateComment(comment);
          }
          return {
            text: comment,
            isSafe: moderation?.isSafe
          };
        })
      );
      
      setGeneratedComments(moderatedVariations);
    } catch (err) {
      setError(err?.message || 'Failed to generate comments');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">AI Comment Configuration</h3>
          <Bot className="w-5 h-5 text-blue-600" />
        </div>

        {/* AI Provider Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            AI Provider
          </label>
          <div className="flex gap-3">
            <button
              onClick={() => setAiProvider('openai')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-colors ${
                aiProvider === 'openai' ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-blue-300'
              }`}
            >
              <Bot className="w-4 h-4" />
              <span className="font-medium">OpenAI (GPT-5)</span>
            </button>
            <button
              onClick={() => setAiProvider('gemini')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-colors ${
                aiProvider === 'gemini' ? 'border-purple-600 bg-purple-50 text-purple-700' : 'border-gray-200 hover:border-purple-300'
              }`}
            >
              <Bot className="w-4 h-4" />
              <span className="font-medium">Gemini 2.5</span>
            </button>
            <button
              onClick={() => setAiProvider('anthropic')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-colors ${
                aiProvider === 'anthropic' ? 'border-orange-600 bg-orange-50 text-orange-700' : 'border-gray-200 hover:border-orange-300'
              }`}
            >
              <Bot className="w-4 h-4" />
              <span className="font-medium">Claude (Sonnet 4)</span>
            </button>
          </div>
        </div>

        {/* Keywords Section */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Target Keywords/Phrases
          </label>
          <div className="flex gap-2 mb-3">
            <Input
              placeholder="Enter keyword or phrase..."
              value={newKeyword}
              onChange={(e) => setNewKeyword(e?.target?.value)}
              onKeyPress={(e) => e?.key === 'Enter' && handleAddKeyword()}
            />
            <Button onClick={handleAddKeyword} size="sm">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          {keywords?.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {keywords?.map((keyword, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                >
                  {keyword}
                  <button
                    onClick={() => handleRemoveKeyword(keyword)}
                    className="hover:bg-blue-200 rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">
              No keywords added. AI will not comment on any posts.
            </p>
          )}
        </div>

        {/* Match Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Keyword Match Type
          </label>
          <Select 
            value={matchType} 
            onChange={(e) => setMatchType(e?.target?.value)}
            options={[
              { value: 'any', label: 'Match ANY keyword (broader reach)' },
              { value: 'all', label: 'Match ALL keywords (more specific)' }
            ]}
          />
          <p className="mt-1 text-xs text-gray-500">
            {matchType === 'any' ? 'AI will comment if post contains at least one keyword' : 'AI will comment only if post contains all keywords'}
          </p>
        </div>

        {/* Comment Style */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Comment Style
          </label>
          <Select 
            value={commentStyle} 
            onChange={(e) => setCommentStyle(e?.target?.value)}
            options={[
              { value: 'supportive', label: 'Supportive & Encouraging' },
              { value: 'insightful', label: 'Insightful & Thoughtful' },
              { value: 'question', label: 'Engaging Questions' },
              { value: 'humorous', label: 'Light-hearted & Fun' }
            ]}
          />
        </div>

        {/* Brand Voice */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Brand Voice
          </label>
          <Select 
            value={brandVoice} 
            onChange={(e) => setBrandVoice(e?.target?.value)}
            options={[
              { value: 'professional', label: 'Professional' },
              { value: 'casual', label: 'Casual & Friendly' },
              { value: 'authoritative', label: 'Authoritative' },
              { value: 'playful', label: 'Playful & Creative' }
            ]}
          />
        </div>

        {/* Rate Limiting */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Max Comments Per Hour
          </label>
          <Input
            type="number"
            min="1"
            max="50"
            value={maxCommentsPerHour}
            onChange={(e) => setMaxCommentsPerHour(parseInt(e?.target?.value) || 10)}
          />
          <p className="mt-1 text-xs text-gray-500">
            Prevents excessive commenting and maintains authenticity
          </p>
        </div>

        {/* Test Comment Section */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Test Post
          </label>
          <Input
            value={testPost}
            onChange={(e) => setTestPost(e?.target?.value)}
            placeholder="Enter a post to test AI commenting..."
          />
        </div>

        {/* Test Button */}
        <div className="mt-4">
          <Button
            onClick={handleTestComment}
            disabled={isGenerating || !testPost?.trim() || !commentStyle}
            className="flex items-center gap-2"
          >
            {isGenerating ? (
              <>
                <Zap className="w-4 h-4 animate-spin" />
                <span>Generating Comments...</span>
              </>
            ) : (
              <>
                <Zap className="w-4 h-4" />
                <span>Test AI Commenting</span>
              </>
            )}
          </Button>
        </div>

        {/* Generated Comments */}
        {generatedComments?.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Generated Comments</h4>
            <div className="space-y-1">
              {generatedComments?.map((comment, index) => (
                <div
                  key={index}
                  className={`p-3 rounded-lg border ${
                    comment?.isSafe 
                      ? 'bg-green-50 border-green-200 text-green-800' :'bg-red-50 border-red-200 text-red-800'
                  }`}
                >
                  <p className="text-sm">{comment?.text}</p>
                  <p className="text-xs mt-1">
                    {comment?.isSafe ? '✅ Safe for posting' : '⚠️ Contains unsafe content'}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-2">
            <Zap className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">How AI Commenting Works:</p>
              <ul className="space-y-1 text-xs">
                <li>• AI analyzes posts for keyword relevance using context</li>
                <li>• Generates authentic, natural comments that add value</li>
                <li>• Moderates content for safety before posting</li>
                <li>• Respects rate limits to maintain authenticity</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex items-center gap-3 mt-4">
          <Button
            onClick={handleSaveConfig}
            className="flex-1"
            disabled={keywords?.length === 0}
          >
            <Save className="w-4 h-4 mr-2" />
            Save Configuration
          </Button>
          {isSaved && (
            <div className="flex items-center gap-1 text-green-600">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm font-medium">Saved!</span>
            </div>
          )}
        </div>

        {keywords?.length === 0 && (
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              Add at least one keyword to enable AI commenting
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AICommentConfig;